"use client";
import { Icon } from "@iconify/react";
import NextLink from "next/link";
import { usePathname } from "next/navigation";
import { motion, easeOut } from "framer-motion";

import { getRootUrl } from "@/utils/getRootUrl";

interface Props {
  setOpenCompanies?: (value: boolean) => void;
}

const CompaniesNav = ({ setOpenCompanies }: Props) => {
  const currentPath = usePathname();

  const menuItems = [
    {
      href: getRootUrl(),
      title: "Cotton Group",
      description:
        "Connecting skilled people and growing businesses together in one trusted ecosystem",
      // icon: "ri:user-fill",
      icon: "ri:global-fill",
      bgColor: "bg-[#1DAF9C]",
      active: false,
    },
    {
      href: getRootUrl("services"),
      title: "Cotton Services",
      description:
        "Cotton Services connects individuals and businesses to verified service providers across multiple categories.",
      icon: "ri:user-fill",
      bgColor: "bg-[#1DAF61]",
      active: false,
    },
    {
      href: getRootUrl("security"),
      title: "Cotton Security",
      description:
        "From personnel to full security frameworks, we help organizations operate with confidence — knowing their people and assets are protected.",
      icon: "ri:shield-star-fill",
      bgColor: "bg-purple-600",
      active: false,
    },
  ];

  // ✅ animation variants
  const container = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.08,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 10 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.25, ease: easeOut },
    },
  };

  return (
    <div>
      {/* 🔹 Header */}
      <p className="text-xs font-medium text-neutral-500 tracking-[0.48px] leading-4 px-3 py-1 uppercase mb-2">
        OUR COMPANIES
      </p>

      {/* ✅ animated container */}
      <motion.div
        animate="show"
        className="flex flex-col "
        initial="hidden"
        variants={container}
      >
        {menuItems.map((itemData) => {
          const isActive = currentPath === itemData.href;

          return (
            <motion.div
              key={itemData.href}
              className={`${itemData.active && "dark:bg-foreground-100/40 bg-foreground-100"} rounded-xl `}
              variants={item}
            >
              <NextLink
                className={`flex gap-4 p-3 rounded-xl max-w-xl pr-16 transition ${
                  isActive
                    ? "bg-foreground-100/400 "
                    : "hover:bg-foreground-100/40"
                }`}
                href={itemData.href}
                onClick={() => setOpenCompanies?.(false)}
              >
                <div
                  className={` w-8 h-8 text-xl rounded-lg ${itemData.bgColor} flex items-center justify-center text-white shrink-0`}
                >
                  <Icon icon={itemData.icon} />
                </div>

                <div className="flex flex-col">
                  <div className="flex items-center max-sm:flex-wrap gap-4 mb-2">
                    <h3
                      className={` text-base font-medium text-neutral-950 dark:text-primary  tracking-[-0.176px] leading-6`}
                    >
                      {itemData.title}
                    </h3>
                  </div>

                  <p className="text-sm leading-5 tracking-[-0.084px] text-sub-600 ">
                    {itemData.description}
                  </p>
                </div>
              </NextLink>
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
};

export default CompaniesNav;
